/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {TeamApiService, TeamModel} from './team-api.service';
import {TeamCopyModalComponent} from './team-copy-modal.component';
import {TeamItemPageComponent} from './team-item-page.component';
import {TeamListPageComponent} from './team-list-page.component';

export function teamStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(TeamApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.axAdvertiserAdmin.apiKey)
    && !permissions.hasNetworkPermission('POLITICAL_ADMIN', null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper('teams.team', TeamApiService, 'app.adm', 'app/admin');
  const copyConfig = {
    modalComponent: TeamCopyModalComponent,
    stateUrl: 'app.adm.teams.team',
    lcase: 'team',
    ucase: 'Team'
  };

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: TeamListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {
      teamPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<TeamModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'Team', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Team', 'name', copyConfig));
      },
      model: function(teamPage: SignalAdminResourcePageState<TeamModel>) {
        return teamPage.model();
      }
    },
    views: {
      'content@app': {component: TeamItemPageComponent, bindings: {state: 'teamPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'teamPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'teamPage'}}
    }
  }));
  return states.build();
}
